/* Settings workspace.

   Bottom rail zone on desktop, an ordinary tab on mobile: §26 keeps settings
   intentionally minimal. It gained an Appearance section at the mobile pass —
   removing the header took the theme toggle with it, and a preference with no
   control is not a preference. Still no density control anywhere.

   Two columns on desktop: account and webhook are short and static, so they share
   a narrow left column; the category editor is the only section with real content
   and takes the rest. Stacks below the breakpoint with no rework.

   Each section is a `.group` (the heading) wrapping a `.card` (the rows), and
   which of the two carries the card treatment flips at the breakpoint — see the
   styles below and ADR 0011. */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import { settingsRow } from './row.js';
import type { Category, Me, ThemePref, WorkspaceDef } from '../../core/types.js';
import {
  authEnabled,
  failedCount,
  getCategories,
  getMe,
  getMeta,
  isSignedIn,
  logout,
} from '../../core/api.js';
import { signOutOfFirebase } from '../../core/firebase.js';
import { purgeUserCaches } from '../../core/cache.js';
import * as theme from '../../core/theme.js';
import { homeUrl } from '../../core/router.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-notice/g-notice.js';
import './settings-account.js';
import './settings-appearance.js';
import './settings-webhook.js';
import './settings-categories.js';

export const settingsDef: WorkspaceDef = {
  id: 'settings',
  label: 'Settings',
  icon: 'settings',
  tag: 'g-settings',
  zone: 'bottom',
  searchable: false,
  identity: true,
  refreshable: true,
};

@customElement('g-settings')
export class GSettings extends LitElement {
  static override styles = [
    shadowReset,
    settingsRow,
    css`
      /* --gutter-outer, not a fixed inset: 24px on desktop (the container owns
         the gutter, the document idiom) and 0 on mobile, where the groups go
         flush to the screen edge and each row insets its own content instead
         (ADR 0009 decision 3). The vertical padding is unconditional — the
         handover is horizontal only. */
      :host {
        display: block;
        padding: var(--space-4) var(--gutter-outer) var(--space-6);
      }
      /* No page title — the rail already names the active workspace, so an <h1>
         repeating it labels the same thing twice (§23). The section headings
         below carry all the structure this page needs. */
      .cols {
        display: grid;
        gap: var(--space-4);
        align-items: start;
      }
      /* Grid items default to min-width:auto, so a nowrap descendant (the webhook
         URL) would set the column's min-content width and overflow the track —
         the neighbouring column then paints over it. This is what lets the
         ellipsis inside actually engage. */
      .cols > * {
        min-width: 0;
      }
      /* BP_DESKTOP (960) — media queries can't read a custom property, so this
         value is mirrored from design/tokens.ts.
         The measure is capped rather than stretched: at 1800px a settings form
         spread edge-to-edge puts each category's count a screen away from its
         name. Whitespace on the right is the better trade. */
      @media (min-width: 960px) {
        .cols {
          grid-template-columns: 300px minmax(0, 760px);
        }
      }
      .stack {
        display: grid;
        gap: var(--space-4);
        align-content: start;
        min-width: 0;
      }
      /* Desktop: the GROUP is the card, with its heading inside it — the shape
         this page has always had. Note the horizontal padding is 0: the rows
         inside own the whole inset, through the one --row-pad-* expression in
         row.ts, so the card interior can't disagree with itself. */
      .group {
        min-width: 0;
        padding: var(--space-3) 0;
        border: 1px solid var(--border);
        border-radius: var(--radius-lg);
        background: var(--surface);
      }
      /* The heading, the captions and every row share ONE left edge — the row
         inset from row.ts — so the group reads as a column rather than three
         indents. Same alignment rule g-index's sticky .kind headers follow. */
      h2,
      .cap {
        padding: 0 var(--row-pad-r) 0
          var(--row-pad-l);
      }
      /* g-index's section-header treatment, in --text (the accent is already
         spent on the category counts and the Add row). The margin is lopsided on
         purpose: 24px of group padding above, 8px below — a heading binds to
         whichever side is closer, and it must bind downward. */
      h2 {
        margin: 0 0 var(--space-2);
        color: var(--text);
        font-family: var(--font-display);
        font-size: 1.05rem;
        font-weight: 700;
      }
      /* Prose that belongs to a group without being a row in it: an introduction
         above the rows, a qualification below them. Rendered here rather than by
         the component, so it can sit outside the list — ADR 0011 decision 6. */
      .cap {
        margin: 0;
        color: var(--text-dim);
        font-size: 0.82rem;
        line-height: 1.4;
      }
      h2 + .cap {
        margin-bottom: var(--space-2);
      }
      .card + .cap {
        margin-top: var(--space-2);
      }
      .cap code {
        color: var(--text);
        font-family: var(--font-mono);
      }
      /* There is no list or retry endpoint yet, so this is a stat and must not
         look openable — a div.row, not a button.row. */
      .stat {
        color: var(--text-dim);
      }
      .stat b {
        margin-left: auto;
        color: var(--text);
        font-family: var(--font-mono);
        font-size: 0.75rem;
      }
      /* Mobile: the card disappears entirely and the page goes flat — rows on
         --bg, groups divided by a seam (ADR 0011 decision 1). Nothing here
         changes at or above 960px, so desktop keeps the arrangement it was
         designed with.

         BP_DESKTOP (960) - 1; media queries can't read a custom property, so the
         value is mirrored from design/tokens.ts. Keep them in sync. */
      @media (max-width: 959px) {
        /* Dissolves the desktop column so its four groups become siblings of
           Categories: what lets the seam rule reach every group with one
           selector, and what makes Imports' order override below possible. */
        .stack {
          display: contents;
        }
        .cols {
          gap: 0;
        }
        .group {
          padding: var(--space-5) 0;
          border: 0;
          border-radius: 0;
          background: none;
        }
        /* THE separator, and the only strong horizontal on the page. It replaces
           both the group gap and the card edge, so one mark says "new group"
           instead of three saying it faintly. */
        .group:not(:first-child) {
          border-top: var(--space-2) solid var(--surface-2);
        }
        .group:first-child {
          padding-top: 0;
        }
        /* Visual order only — in the DOM Imports stays at the foot of the short
           left column, rather than stranded in a second grid row beneath a
           column three times its height. */
        .imports {
          order: 1;
        }
      }
    `,
  ];

  /** Declared by WorkspaceDef but unused — Settings isn't searchable. */
  @property() query = '';

  @state() private me: Me | null = null;
  @state() private categories: Category[] = [];
  @state() private failed = 0;
  @state() private authEnabled = false;
  /** Re-read from core/theme on change; the header can move it too. */
  @state() private themePref: ThemePref = theme.getPref();
  private unwatchPref?: () => void;
  @state() private loaded = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.unwatchPref = theme.subscribe(() => {
      this.themePref = theme.getPref();
    });
    void this.load();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unwatchPref?.();
  }

  /**
   * Refetch account, categories and the failed-import count. Called by the
   * shell's pull-to-refresh — see `WorkspaceDef.refreshable` and ADR 0010.
   *
   * Worth having despite Settings being mostly static: the Imports stat is the
   * one number here that moves on its own, as the background worker drains the
   * queue.
   */
  async refresh(): Promise<void> {
    await this.load();
  }

  private async load(): Promise<void> {
    const [me, cats, auth] = await Promise.all([getMe(), getCategories(), authEnabled()]);
    if (isSignedIn(me)) this.me = me;
    if (isSignedIn(cats)) this.categories = cats;
    this.authEnabled = auth;
    await this.refreshCounts();
    this.loaded = true;
  }

  /** The failed-import total lives on /api/meta. */
  private async refreshCounts(): Promise<void> {
    try {
      const meta = await getMeta();
      if (isSignedIn(meta)) this.failed = failedCount(meta);
    } catch {
      /* the stat is informational — never let it break the page */
    }
  }

  private async reloadCategories(): Promise<void> {
    const cats = await getCategories();
    if (isSignedIn(cats)) this.categories = cats;
    await this.refreshCounts();
  }

  /**
   * Drop the server session, then Firebase's.
   *
   * Both halves are needed. Our cookie is what actually gates access, but
   * leaving Firebase signed in makes the button look broken: the next popup
   * re-selects the same account without asking and lands you straight back in.
   *
   * Then drop this account's cached thumbnails, awaited rather than fired off:
   * the navigation below would otherwise tear the page down mid-delete and leave
   * them for whoever signs in next.
   *
   * Then go home rather than reload. Reloading leaves the URL on /settings, so
   * the next sign-in resumes into the previous session's workspace — the route
   * is session state, and a new session starts at the default (§12).
   */
  private async signOut(): Promise<void> {
    await logout().catch(() => undefined);
    await signOutOfFirebase();
    await purgeUserCaches();
    location.assign(homeUrl());
  }

  override render() {
    return html`
      <div class="cols">
        <div class="stack">
          <div class="group">
            <h2>Account</h2>
            <div class="card">
              <settings-account
                .me=${this.me}
                .authEnabled=${this.authEnabled}
                @sign-out=${this.signOut}
              ></settings-account>
            </div>
          </div>

          <div class="group">
            <h2>Appearance</h2>
            <div class="card">
              <settings-appearance
                .pref=${this.themePref}
                @theme-pref=${(e: CustomEvent<ThemePref>) => theme.setPref(e.detail)}
              ></settings-appearance>
            </div>
            ${this.themePref === 'auto'
              ? html`<p class="cap">
                  Following your system, currently ${theme.resolve('auto')}.
                </p>`
              : ''}
          </div>

          <div class="group">
            <h2>Share-sheet webhook</h2>
            <p class="cap">
              POST <code>{"url": "…"}</code> here from your phone's shortcut app to save
              into your library.
            </p>
            <div class="card">
              <settings-webhook .url=${this.me?.webhook_url ?? ''}></settings-webhook>
            </div>
            <g-notice class="cap" tone="warn">
              Anyone with this URL can add items to your library.
            </g-notice>
          </div>

          <div class="group imports">
            <h2>Imports</h2>
            <div class="card">
              <div class="row stat">
                <g-icon name="warn" style="--icon-size:15px"></g-icon>
                Failed imports <b>${this.failed}</b>
              </div>
            </div>
          </div>
        </div>

        <div class="group">
          <h2>Categories</h2>
          <p class="cap">
            Categories steer how <strong>new</strong> saves are classified. Nothing already
            saved is re-classified.
          </p>
          <div class="card">
            ${this.loaded
              ? html`<settings-categories
                  .categories=${this.categories}
                  @changed=${this.reloadCategories}
                ></settings-categories>`
              : html`<g-notice class="text">Loading…</g-notice>`}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-settings': GSettings;
  }
}
